import { AxiosError } from 'axios';
import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Button from '@/components/Button/Button';
import Card from '@/components/Card/Card';
import Header from '@/components/Header/Header';
import Table from '@/components/Tables/Table/Table';
import Tag from '@/components/Tag/Tag';
import { TagTypes } from '@/components/Tag/Tag.types';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useAdminSubmissionDetails, useCompleteSubmissions } from '@/services/submissions/submissions.service';
import { AdminSubmission, SubmissionStatus } from '@/types/submission.types';
import useNavigateWithToast from '@/utils/useNavigateWithToast/useNavigateWithToast';
import styles from './SubmissionDetails.module.css';
import { useTranslation } from 'react-i18next';
import TooltipDateLabel from '@/components/DateLabel/TooltipDateLabel';

const SubmissionDetails: FC = () => {
  const { t } = useTranslation();
  const { orgId, submissionId } = useParams();
  const { data, isError, isLoading } = useAdminSubmissionDetails(orgId !== undefined, orgId!, submissionId);
  const isComplete = data?.data.status === SubmissionStatus.COMPLETED;
  const { mutate, isLoading: completeIsLoading, isSuccess } = useCompleteSubmissions();
  const isNationalProgram = data?.data.nationalProgramId;

  const navigateWithToast = useNavigateWithToast();

  const handleClick = () => {
    if (data?.data.orgId) {
      //If it's a NationalProgram, then append submissionId.
      const currentSubmissionId = data.data?.submissionId;
      let orgIdToUpdate: string = data.data.orgId;
      if (data.data.orgId === 'ALL') {
        orgIdToUpdate = `ALL_${currentSubmissionId}`;
      }
      mutate([orgIdToUpdate], {
        onSuccess: (responseData) => {
          let resSubmissionId: string = responseData.data[0].id;
          if (data.data.orgId === 'ALL') {
            //find by submission
            const completedSubmission = responseData.data.find(
              (item: AdminSubmission) => item.submissionId === currentSubmissionId,
            );
            resSubmissionId = completedSubmission.id;
          }
          navigateWithToast(`/submissions/${responseData.data[0].orgId}/${resSubmissionId}`, {
            toastType: ToastTypes.Success,
            label: t('submissions.extractMarkedComplete'),
            id: 'completeOneSubmissionSuccess',
          });
        },
        onError: (error) => {
          showToast({
            toastType: ToastTypes.Error,
            label: (error as AxiosError).message || t('error.markingSubmissionComplete'),
            id: 'completeOneSubmissionError',
          });
        },
      });
    }
  };

  useEffect(() => {
    if (isError && !isSuccess)
      navigateWithToast('/submissions', {
        toastType: ToastTypes.Error,
        label: t('submissions.noPendingSubmissions'),
        id: 'noPendingSubmissions',
      });
  }, [isError, isSuccess, navigateWithToast, t]);

  return (
    <>
      <Header
        title={t('extractQueue.details.title')}
        breadcrumb={{ to: '/submissions', label: t('breadcrumb.backToExtractRequestQueue') }}
      />
      <div className={styles.page}>
        <div className={styles.statusRow}>
          <div>
            <span className={styles.status}>{t('common.status')}:</span>
            {isLoading ? (
              <span className="skeleton" data-testid="loadingSkeleton" />
            ) : (
              <Tag
                type={isComplete ? TagTypes.Completed : TagTypes.Pending}
                label={t(`submissionStatusLabels.${data?.data.status || 'PENDING'}`)}
              />
            )}
          </div>
          {!isComplete && (
            <Button isLoading={completeIsLoading} onClick={handleClick}>
              {t('extractQueue.markCompleteButton')}
            </Button>
          )}
        </div>
        <Card className={styles.detailsCard}>
          <h2 className={styles.cardHeader}>{t('extractQueue.details.title')}</h2>
          <div className={styles.extractDetail}>
            <span className={styles.bold}>{t('extractQueue.table.orgId')}:</span>
            {isLoading ? <span className="skeleton" /> : <span>{data?.data.fzCode}</span>}
          </div>
          <div className={styles.extractDetail}>
            <span className={styles.bold}>{t('extractQueue.table.lastChange')}:</span>
            {isLoading ? <span className="skeleton" /> : <TooltipDateLabel date={data?.data.lastChangeDate} />}
          </div>
          {data?.data.nationalProgramName && (
            <div className={styles.extractDetail}>
              <span className={styles.bold}>{t('extractQueue.table.nationalProgram')}:</span>
              {isLoading ? <span className="skeleton" /> : <span>{data?.data.nationalProgramName}</span>}
            </div>
          )}
          {data?.data.nationalProgramType && (
            <div className={styles.extractDetail}>
              <span className={styles.bold}>{t('extractQueue.table.type')}:</span>
              {isLoading ? (
                <span className="skeleton" />
              ) : (
                <span>{data?.data.nationalProgramType.replaceAll('_', ' ')}</span>
              )}
            </div>
          )}
        </Card>
        {!isNationalProgram && (
          <Card className={styles.storesCard}>
            <h2 className={styles.cardHeader}>
              {isComplete ? t('extractQueue.details.storesAffected') : t('extractQueue.details.storesToBeExtracted')}
            </h2>
            <div className={styles.storesTable}>
              <Table
                data={data?.data?.storesAffected.map((storeNo) => ({ storeNo })) || []}
                columns={[{ header: t('extractQueue.table.storeNo'), accessorKey: 'storeNo' }]}
                includeMinWidth={false}
                isLoading={isLoading}
              />
            </div>
          </Card>
        )}
      </div>
    </>
  );
};

export default SubmissionDetails;
